/* eslint-disable react/prop-types */
import { createContext, useContext, useEffect, useId, useRef, useState } from "react";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  Button,
  Box,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import SoftAlert from "components/SoftAlert";
import { MENSAJE_LEGAJO_BLOQUEADO } from "utils/empresaEstado";
const erroresContext = createContext([]);
const secciones = [
  "Participantes",
  "Vehículo",
  "Residuos",
  "Manipulación y peligrosidad",
  "Tratamiento / disposición",
  "Anexos y emergencias",
];
export function camposPendientes(m, anexos = 0) {
  const d = m.datos || {};
  const faltantes = (pairs, data) =>
    pairs.filter(([key]) => !String(data[key] ?? "").trim()).map(([, label]) => label);
  return [
    faltantes(
      [
        ["idGenerador", "Generador"],
        ["idTransportista", "Transportista"],
        ["idOperador", "Operador"],
      ],
      m
    ),
    d.idVehiculo ? [] : ["Vehículo"],
    !d.residuos?.length
      ? ["Agregar al menos un residuo"]
      : [
          ...new Set(
            d.residuos.flatMap((r) => [
              ...faltantes(
                [
                  ["idResiduo", "Residuo del catálogo"],
                  ["descripcion", "Descripción / clase"],
                  ["unidad", "Unidad de medida"],
                  ["estadoFisico", "Estado físico"],
                ],
                r
              ),
              ...(!Number.isFinite(Number(r.cantidad)) || Number(r.cantidad) <= 0
                ? ["Cantidad"]
                : []),
            ])
          ),
        ],
    [],
    faltantes([["idTratamiento", "Tratamiento requerido"]], d),
    [],
  ];
}
function Seccion({ index, activa, id, errores, children }) {
  return (
    <Box
      role="tabpanel"
      id={`${id}-panel-${index}`}
      aria-labelledby={`${id}-tab-${index}`}
      hidden={activa !== index}
    >
      <Stack spacing={3}>
        {errores.length > 0 && <Alert severity="error">Completá: {errores.join(", ")}.</Alert>}
        {children}
      </Stack>
    </Box>
  );
}
export const empty = {
  residuos: [],
  vehiculo: {
    tipo: "",
    dominio: "",
    habilitacion: "",
    vencimientoHabilitacion: "",
    poliza: "",
    vencimientoSeguro: "",
  },
  componentes: "",
  inhalacion: false,
  dermica: false,
  oral: false,
  inflamabilidad: false,
  toxicidad: false,
  reactividad: false,
  instruccionesEspeciales: false,
  instruccionesOperador: "",
  telefonoOperador: "",
  telefonoTransportista: "",
  sinAnexos: "",
};
export function Campo({
  label,
  value,
  onChange,
  options,
  autocomplete = false,
  multiline = false,
  type = "text",
  required = false,
  readOnly = false,
}) {
  const id = useId();
  const [touched, setTouched] = useState(false);
  const pendientes = useContext(erroresContext);
  if (readOnly) {
    const texto = type === "date" && /^\d{4}-\d{2}-\d{2}$/.test(value || "")
      ? value.split("-").reverse().join("/")
      : value;
    return (
      <Box>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body2" color="dark.main">{texto || "Pendiente"}</Typography>
      </Box>
    );
  }
  const error =
    (pendientes.includes(label) &&
      (!String(value ?? "").trim() || (type === "number" && Number(value) <= 0))) ||
    (touched && required && !String(value ?? "").trim()) ||
    (type === "number" &&
      value !== "" &&
      value !== null &&
      value !== undefined &&
      (!Number.isFinite(Number(value)) || Number(value) <= 0));
  return (
    <div>
      <InputLabel htmlFor={id}>
        <SoftTypography component="span" variant="caption" fontWeight="bold">
          {label}
          {required && (
            <SoftTypography component="span" variant="caption" color="primary">
              {" "}
              *
            </SoftTypography>
          )}
        </SoftTypography>
      </InputLabel>
      {options && autocomplete ? (
        <Autocomplete
          fullWidth
          id={id}
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": { height: 60 },
            "& .MuiAutocomplete-input": { padding: "3px 4px !important" },
          }}
          options={options}
          value={options.find((o) => String(o.id) === String(value ?? "")) || null}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.label}
            </li>
          )}
          onChange={(_, option) => onChange(option?.id ?? "")}
          onBlur={() => setTouched(true)}
          noOptionsText={
            label === "Vehículo" ? "No hay vehículos coincidentes" : "No hay empresas coincidentes"
          }
          clearText="Limpiar"
          openText={label === "Vehículo" ? "Mostrar vehículos" : "Mostrar empresas"}
          closeText="Cerrar"
          renderInput={(params) => (
            <TextField
              {...params}
              error={Boolean(error)}
              placeholder={label === "Vehículo" ? "Buscar vehículo" : "Buscar empresa"}
            />
          )}
        />
      ) : options ? (
        <Select
          fullWidth
          displayEmpty
          id={id}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setTouched(true)}
          size="small"
          error={Boolean(error)}
          sx={{ height: 40 }}
        >
          {options.map((o) => (
            <MenuItem key={o.id} value={o.id}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      ) : (
        <SoftInput
          id={id}
          fullWidth
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setTouched(true)}
          multiline={multiline}
          minRows={multiline ? 2 : undefined}
          type={type}
          error={Boolean(error)}
          inputProps={{
            "aria-invalid": Boolean(error),
            "aria-describedby": error ? `${id}-error` : undefined,
          }}
        />
      )}
      {error && (
        <FormHelperText id={`${id}-error`} error>
          {type === "number" ? "Ingresá un valor mayor que cero." : "Completá este campo."}
        </FormHelperText>
      )}
    </div>
  );
}
export default function Formulario({
  initial,
  options,
  residuos,
  tratamientos,
  onSave,
  onCancel,
  busy,
  validar = false,
  declaracionPresentacion = "",
  anexosExistentes = 0,
}) {
  const tabsId = useId();
  const paneles = useRef(null);
  const [seccion, setSeccion] = useState(() =>
    validar
      ? Math.max(
          0,
          camposPendientes(initial, anexosExistentes).findIndex((e) => e.length)
        )
      : 0
  );
  const [validacionActiva, setValidacionActiva] = useState(validar);
  const [dialogoPresentacion, setDialogoPresentacion] = useState(false);
  const [form, setForm] = useState(() =>
    initial
      ? {
          ...initial,
          datos: {
            ...initial.datos,
            ...Object.fromEntries(
              ["inhalacion", "dermica", "oral", "inflamabilidad", "toxicidad", "reactividad", "instruccionesEspeciales"].map(
                (key) => [key, initial.datos?.[key] === true]
              )
            ),
          },
        }
      : { idGenerador: "", idTransportista: "", idOperador: "", datos: empty }
  );
  const [vehiculoSeleccionado, setVehiculoSeleccionado] = useState(initial?.datos?.idVehiculo || "");
  const [residuoAQuitar, setResiduoAQuitar] = useState(null);
  const [archivos, setArchivos] = useState([]);
  const [tipoArchivo, setTipoArchivo] = useState("Contingencia");
  const [archivoError, setArchivoError] = useState("");
  const d = form.datos;
  const generadorBloqueado = options.empresas.some(e => String(e.id) === String(form.idGenerador) && e.estado === "Irregular");
  const empresasBloqueadas = options.empresas.filter(e => e.estado === "Irregular" &&
    [form.idGenerador, form.idTransportista, form.idOperador].some(id => String(id) === String(e.id)));
  const errores = validacionActiva
    ? camposPendientes(form, archivos.length + anexosExistentes)
    : secciones.map(() => []);
  useEffect(() => {
    if (validacionActiva)
      paneles.current
        ?.querySelector('[role="tabpanel"]:not([hidden]) [aria-invalid="true"]')
        ?.focus();
  }, [validacionActiva, seccion]);
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const dato = (key, value) => setForm((f) => ({ ...f, datos: { ...f.datos, [key]: value } }));
  const changeResiduo = (index, key, value) =>
    dato(
      "residuos",
      d.residuos.map((r, i) => (i === index ? { ...r, [key]: value } : r))
    );
  const solicitarPresentacion = () => {
    if (empresasBloqueadas.length) return;
    const primeraPendiente = camposPendientes(form, archivos.length + anexosExistentes).findIndex(
      (pendientes) => pendientes.length
    );
    if (primeraPendiente >= 0) {
      setValidacionActiva(true);
      setSeccion(primeraPendiente);
    } else setDialogoPresentacion(true);
  };
  const confirmarPresentacion = async () => {
    const pendientes = [...archivos];
    const guardado = await onSave(form, pendientes, true);
    setArchivos(pendientes);
    if (guardado !== false) setDialogoPresentacion(false);
  };
  const companies = (rol) =>
    options.empresas
      .filter((e) => e.actividad?.includes(rol) && (rol !== "Generador" || e.propia) &&
        (e.estado !== "Irregular" || String(e.id) === String(form[`id${rol}`])))
      .map((e) => ({ id: e.id, label: e.razonSocial }));
  const camposVehiculo = [
    ["tipo", "Tipo de vehículo"],
    ["marcaModelo", "Marca y modelo"],
    ["dominio", "Dominio / patente"],
    ["habilitacion", "Número de habilitación"],
    ["vencimientoHabilitacion", "Vencimiento de habilitación"],
    ["poliza", "Póliza de seguro"],
    ["vencimientoSeguro", "Vencimiento del seguro"],
  ];
  const disponibles = (options.vehiculos || []).filter(
    (v) => String(v.idEmpresa) === String(form.idTransportista)
  );
  const vehiculo = disponibles.find((v) => String(v.id) === String(d.idVehiculo)) || {};
  const seleccionarVehiculo = (id) => {
    const elegido = disponibles.find((v) => String(v.id) === String(id));
    setVehiculoSeleccionado(id);
    setForm((f) => ({
      ...f,
      datos: {
        ...f.datos,
        idVehiculo: elegido?.id || null,
        vehiculo: elegido
          ? Object.fromEntries(camposVehiculo.map(([k]) => [k, elegido[k] || ""]))
          : { ...empty.vehiculo },
      },
    }));
  };
  return (
    <erroresContext.Provider value={errores.flat()}>
      <Card ref={paneles}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Stack
            spacing={3}
            sx={{
              "& .MuiTypography-subtitle1": {
                fontWeight: 700,
                color: "primary.main",
                borderBottom: 1,
                borderColor: "divider",
                pb: 1,
                pt: 1,
              },
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
              <Typography variant="h6">
                {form.id ? "Editar borrador" : "Nuevo manifiesto"}
              </Typography>
              {onCancel && (
                <SoftButton
                  variant="outlined"
                  color="primary"
                  startIcon={<CloseIcon />}
                  disabled={busy}
                  sx={{ textTransform: "none" }}
                  onClick={onCancel}
                >
                  {form.id ? "Salir sin guardar" : "Cancelar"}
                </SoftButton>
              )}
            </Stack>
            <Alert severity="info">
              El borrador puede quedar incompleto. Al presentarlo se fija su contenido y se asigna
              número.
            </Alert>
            {empresasBloqueadas.length > 0 && (
              <SoftAlert color="error">
                <SoftTypography variant="caption" color="white">
                  {generadorBloqueado ? MENSAJE_LEGAJO_BLOQUEADO
                    : `No se puede presentar el manifiesto. Legajos bloqueados: ${empresasBloqueadas.map(e => e.razonSocial).join(", ")}.`}
                </SoftTypography>
              </SoftAlert>
            )}
            <Tabs
              value={seccion}
              onChange={(_, value) => setSeccion(value)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-label="Secciones del formulario de manifiesto"
              sx={{
                bgcolor: "transparent",
                borderRadius: 0,
                borderBottom: 1,
                borderColor: "divider",
                p: 0,
                minHeight: 48,
                "& .MuiTabs-indicator": {
                  height: 3,
                  bottom: 0,
                  bgcolor: "primary.main",
                  boxShadow: "none",
                  transition: "none",
                },
                "& .MuiTab-root": { flex: "none", minHeight: 48, px: 2, py: 1, fontWeight: 600 },
                "& .MuiTab-root.Mui-selected": { color: "primary.main !important" },
                "& .MuiTab-root:focus-visible": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: -2,
                },
              }}
            >
              {secciones.map((label, index) => (
                <Tab
                  key={label}
                  id={`${tabsId}-tab-${index}`}
                  aria-controls={`${tabsId}-panel-${index}`}
                  label={`${index + 1}. ${label}${errores[index].length ? " (pendiente)" : ""}`}
                />
              ))}
            </Tabs>
            <Seccion index={0} activa={seccion} id={tabsId} errores={errores[0]}>
              <Typography variant="subtitle1">1. Participantes</Typography>
              <Grid container spacing={2}>
                {["Generador", "Transportista", "Operador"].map((rol) => (
                  <Grid item xs={12} md={4} key={rol}>
                    <Campo
                      label={rol}
                      value={form[`id${rol}`]}
                      onChange={(v) => {
                        if (rol === "Transportista" && String(v) !== String(form.idTransportista)) {
                          setVehiculoSeleccionado("");
                          setForm((f) => ({
                            ...f,
                            idTransportista: v,
                            datos: {
                              ...f.datos,
                              idVehiculo: null,
                              vehiculo: { ...empty.vehiculo },
                            },
                          }));
                        } else set(`id${rol}`, v);
                      }}
                      autocomplete
                      options={companies(rol)}
                    />
                    {form[`id${rol}`] &&
                      (() => {
                        const i = options.inscripciones.find(
                          (row) =>
                            String(row.idEmpresa) === String(form[`id${rol}`]) &&
                            row.actividad === rol
                        );
                        return (
                          <Typography variant="caption">
                            Legajo: {form[`id${rol}`]} · C.A.A.: {i?.caa || "pendiente"} · Vto.:{" "}
                            {i?.vencimiento || "pendiente"}
                          </Typography>
                        );
                      })()}
                  </Grid>
                ))}
              </Grid>
            </Seccion>
            <Seccion index={1} activa={seccion} id={tabsId} errores={errores[1]}>
              <Typography variant="subtitle1">2. Vehículo de la transportista</Typography>
              <Typography variant="body2">
                Seleccioná un vehículo registrado de la transportista. La transportista podrá
                aceptar o rechazar el traslado.
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "flex-end" }}
              >
                <Box sx={{ flex: 1 }}>
                  <Campo
                    label="Vehículo"
                    autocomplete
                    value={vehiculoSeleccionado}
                    onChange={seleccionarVehiculo}
                    options={[
                      ...disponibles.map((v) => ({
                        id: v.id,
                        label: [v.tipo, v.dominio, v.marcaModelo].filter(Boolean).join(" · "),
                      })),
                    ]}
                  />
                </Box>
              </Stack>
              <Typography variant="caption" color="dark.main" sx={{ mt: "8px !important" }}>
                Buscá por tipo, dominio o marca y modelo.
              </Typography>
              {form.idTransportista && (
                <Typography variant="body2">
                  La administración carga la habilitación y el seguro en Manifiestos &gt; Empresas y
                  vehículos.
                </Typography>
              )}
              {!form.idTransportista && (
                <Typography variant="body2">Seleccioná primero la transportista.</Typography>
              )}
              {form.idTransportista && !disponibles.length && (
                <Typography variant="body2">
                  La transportista no tiene vehículos registrados. Pedile a la administración que
                  registre la unidad y complete su habilitación y seguro en Empresas y vehículos.
                </Typography>
              )}
              {vehiculo.tipo && (
                <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 2 }}>
                  <Typography variant="body2" color="dark.main" sx={{ fontWeight: 700 }}>
                    {vehiculo.tipo} · {vehiculo.dominio}
                  </Typography>
                  <Typography variant="body2" color="dark.main">
                    Habilitación: {vehiculo.habilitacion || "Pendiente"} · Vence:{" "}
                    {vehiculo.vencimientoHabilitacion || "Pendiente"}
                  </Typography>
                  <Typography variant="body2" color="dark.main">
                    Póliza: {vehiculo.poliza || "Pendiente"} · Seguro hasta:{" "}
                    {vehiculo.vencimientoSeguro || "Pendiente"}
                  </Typography>
                </Box>
              )}
            </Seccion>
            <Seccion index={2} activa={seccion} id={tabsId} errores={errores[2]}>
              <Typography variant="subtitle1">3. Información de residuos</Typography>
              {d.residuos.map((r, index) => (
                <Card
                  variant="outlined"
                  key={index}
                  sx={{
                    border: "1px solid",
                    borderColor: "secondary.main",
                    bgcolor: "background.default",
                    boxShadow: "none",
                  }}
                >
                  <CardContent>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={1}
                      sx={{ mb: 2 }}
                    >
                      <Typography variant="body2" sx={{ color: "dark.main", fontWeight: 700 }}>
                        Residuo {index + 1}
                      </Typography>
                      <SoftButton
                        variant="text"
                        color="error"
                        endIcon={<CloseIcon />}
                        disabled={busy}
                        aria-label={`Quitar residuo ${index + 1}`}
                        sx={{
                          "&&": { color: "error.main" },
                          textTransform: "none",
                          px: 1.5,
                          py: 0.75,
                          minHeight: { xs: 44, sm: 34 },
                        }}
                        onClick={() => setResiduoAQuitar(index)}
                      >
                        Quitar
                      </SoftButton>
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Campo
                          label="Residuo del catálogo"
                          value={r.idResiduo}
                          options={residuos.map((x) => ({
                            id: x.id,
                            label: `${x.item} - ${x.descripcion}`,
                          }))}
                          onChange={(v) =>
                            dato(
                              "residuos",
                              d.residuos.map((x, i) =>
                                i === index
                                  ? {
                                      ...x,
                                      idResiduo: v,
                                      descripcion:
                                        residuos.find((y) => y.id === v)?.descripcion || "",
                                    }
                                  : x
                              )
                            )
                          }
                        />
                      </Grid>
                      {[
                        ["Descripción / clase", "descripcion"],
                        ["Cantidad", "cantidad"],
                        ["Unidad de medida", "unidad"],
                        ["Estado físico", "estadoFisico"],
                      ].map(([label, key]) => (
                        <Grid item xs={12} md={key === "descripcion" ? 12 : 3} key={key}>
                          <Campo
                            label={label}
                            value={r[key]}
                            type={key === "cantidad" ? "number" : "text"}
                            onChange={(v) => changeResiduo(index, key, v)}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              ))}
              <SoftButton
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                disabled={busy}
                sx={{
                  alignSelf: "flex-end",
                  textTransform: "none",
                  px: 1.5,
                  py: 0.75,
                  minHeight: { xs: 44, sm: 34 },
                }}
                onClick={() =>
                  dato("residuos", [
                    ...d.residuos,
                    {
                      idResiduo: "",
                      descripcion: "",
                      cantidad: "",
                      unidad: "",
                      estadoFisico: "",
                    },
                  ])
                }
              >
                Agregar residuo
              </SoftButton>
            </Seccion>
            <Seccion index={3} activa={seccion} id={tabsId} errores={errores[3]}>
              <Typography variant="subtitle1">4. Manipulación y peligrosidad</Typography>
              {[
                ["Componentes y características peligrosas", "componentes"],
              ].map(([label, key]) => (
                <Campo
                  key={key}
                  label={label}
                  value={d[key]}
                  onChange={(v) => dato(key, v)}
                  multiline
                />
              ))}
              <Typography variant="body2">Toxicidad</Typography>
              <Grid container spacing={2}>
                {[
                  ["inhalacion", "Por inhalación"],
                  ["dermica", "Dérmica"],
                  ["oral", "Oral"],
                ].map(([key, label]) => (
                  <Grid item xs={12} md={4} key={key}>
                    <FormControlLabel label={label} control={
                      <Checkbox color="primary" checked={d[key] === true}
                        onChange={(e) => dato(key, e.target.checked)} />
                    } />
                  </Grid>
                ))}
              </Grid>
              <Typography variant="body2">Sistema de identificación de peligrosidad</Typography>
              <Grid container spacing={2}>
                {[
                  ["inflamabilidad", "Inflamabilidad"],
                  ["toxicidad", "Toxicidad"],
                  ["reactividad", "Reactividad"],
                  ["instruccionesEspeciales", "Instrucciones especiales"],
                ].map(([key, label]) => (
                  <Grid item xs={12} md={6} key={key}>
                    <FormControlLabel
                      label={label}
                      control={
                        <Checkbox
                          color="primary"
                          checked={d[key] === true}
                          onChange={(e) => dato(key, e.target.checked)}
                        />
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Seccion>
            <Seccion index={4} activa={seccion} id={tabsId} errores={errores[4]}>
              <Typography variant="subtitle1">5. Tratamiento o disposición final</Typography>
              <Campo
                label="Tratamiento requerido"
                value={d.idTratamiento}
                onChange={(v) => dato("idTratamiento", v)}
                options={tratamientos.map((t) => ({
                  id: t.id,
                  label: `${t.item} - ${t.descripcion}`,
                }))}
              />
              <Campo
                label="Instrucciones para el operador"
                value={d.instruccionesOperador}
                onChange={(v) => dato("instruccionesOperador", v)}
                multiline
              />
            </Seccion>
            <Seccion index={5} activa={seccion} id={tabsId} errores={errores[5]}>
              <Typography variant="subtitle1">6. Documentación anexa</Typography>
              <Typography variant="body2">
                Seleccioná los PDF ahora; se subirán al guardar el borrador.
              </Typography>
              <Box sx={{ maxWidth: 440 }}>
                <Campo
                  label="Tipo de documentación"
                  value={tipoArchivo}
                  onChange={setTipoArchivo}
                  options={["Contingencia", "Otro"].map((tipo) => ({ id: tipo, label: tipo }))}
                />
              </Box>
              <SoftButton
                component="label"
                variant="outlined"
                color="primary"
                sx={{ alignSelf: "flex-start" }}
              >
                Seleccionar PDF
                <input
                  hidden
                  type="file"
                  multiple
                  accept="application/pdf,.pdf"
                  aria-label="Seleccionar documentación PDF"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    e.target.value = "";
                    if (
                      files.some(
                        (file) => !file.name.toLowerCase().endsWith(".pdf") || file.size > 10000000
                      )
                    ) {
                      setArchivoError("Solo se admiten PDF de hasta 10 MB cada uno.");
                      return;
                    }
                    setArchivoError("");
                    setArchivos((prev) => [
                      ...prev,
                      ...files.map((archivo) => ({ tipo: tipoArchivo, archivo })),
                    ]);
                  }}
                />
              </SoftButton>
              {archivoError && <Alert severity="error">{archivoError}</Alert>}
              {archivos.map(({ tipo, archivo }, index) => (
                <Typography variant="body2" key={`${archivo.name}-${index}`}>
                  {tipo}: {archivo.name}{" "}
                  <Button
                    color="error"
                    onClick={() => setArchivos((prev) => prev.filter((_, i) => i !== index))}
                  >
                    Quitar
                  </Button>
                </Typography>
              ))}
              <Campo
                label="Si no hay anexos, indicar por qué no corresponde"
                value={d.sinAnexos}
                onChange={(v) => dato("sinAnexos", v)}
                multiline
              />
              <Typography variant="subtitle1">7. Información de emergencia</Typography>
              <Campo
                label="Teléfono del operador"
                value={d.telefonoOperador}
                onChange={(v) => dato("telefonoOperador", v)}
              />
              <Campo
                label="Teléfono del transportista"
                value={d.telefonoTransportista}
                onChange={(v) => dato("telefonoTransportista", v)}
              />
            </Seccion>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="space-between"
              sx={{
                position: "sticky",
                bottom: 0,
                zIndex: 2,
                bgcolor: "background.paper",
                py: 2,
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <Stack direction="row" spacing={1}>
                {seccion > 0 && (
                  <SoftButton
                    variant="outlined"
                    color="primary"
                    startIcon={<ArrowBackIcon />}
                    sx={{ textTransform: "none" }}
                    disabled={busy}
                    onClick={() => setSeccion((s) => s - 1)}
                  >
                    Anterior
                  </SoftButton>
                )}
                <SoftButton
                  variant="contained"
                  color="primary"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ textTransform: "none" }}
                  disabled={busy || seccion === secciones.length - 1}
                  onClick={() => setSeccion((s) => s + 1)}
                >
                  Siguiente
                </SoftButton>
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <SoftButton
                  variant="outlined"
                  color="primary"
                  disabled={busy || generadorBloqueado}
                  onClick={async () => {
                    const pendientes = [...archivos];
                    await onSave(form, pendientes);
                    setArchivos(pendientes);
                  }}
                >
                  {busy ? "Guardando…" : "Guardar borrador"}
                </SoftButton>
                <SoftButton
                  variant="gradient"
                  color="primary"
                  disabled={busy || empresasBloqueadas.length > 0}
                  onClick={solicitarPresentacion}
                >
                  {busy ? "Procesando…" : "Guardar y presentar"}
                </SoftButton>
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
        <Dialog
          open={dialogoPresentacion}
          onClose={() => !busy && setDialogoPresentacion(false)}
          fullWidth
          maxWidth="sm"
          aria-labelledby="presentar-manifiesto-title"
        >
          <DialogTitle id="presentar-manifiesto-title">Presentar manifiesto</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Alert severity="info">
                Al presentar se fija el contenido, se asigna el número y se solicita la aprobación
                de la transportista.
              </Alert>
              <Typography variant="body2">
                ¿Querés guardar y presentar este manifiesto ahora?
              </Typography>
              {declaracionPresentacion && (
                <Alert severity="info">{declaracionPresentacion}</Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <SoftButton
              variant="outlined"
              color="primary"
              disabled={busy}
              onClick={() => setDialogoPresentacion(false)}
            >
              Cancelar
            </SoftButton>
            <SoftButton
              variant="gradient"
              color="primary"
              disabled={busy}
              onClick={confirmarPresentacion}
            >
              {busy ? "Procesando…" : "Confirmar y presentar"}
            </SoftButton>
          </DialogActions>
        </Dialog>
        <Dialog
          open={residuoAQuitar !== null}
          onClose={() => setResiduoAQuitar(null)}
          fullWidth
          maxWidth="xs"
          aria-labelledby="quitar-residuo-title"
        >
          <DialogTitle id="quitar-residuo-title">Quitar residuo</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="dark.main">
              ¿Querés quitar el residuo {residuoAQuitar !== null ? residuoAQuitar + 1 : ""}
              {d.residuos[residuoAQuitar]?.descripcion
                ? ` (${d.residuos[residuoAQuitar].descripcion})`
                : ""}{" "}
              del formulario?
            </Typography>
          </DialogContent>
          <DialogActions>
            <SoftButton variant="outlined" color="primary" onClick={() => setResiduoAQuitar(null)}>
              Cancelar
            </SoftButton>
            <SoftButton
              variant="contained"
              color="error"
              disabled={busy}
              startIcon={<CloseIcon />}
              onClick={() => {
                dato(
                  "residuos",
                  d.residuos.filter((_, index) => index !== residuoAQuitar)
                );
                setResiduoAQuitar(null);
              }}
            >
              Quitar residuo
            </SoftButton>
          </DialogActions>
        </Dialog>
      </Card>
    </erroresContext.Provider>
  );
}
